import type { JSX } from 'solid-js';
import { children, For, Match, Switch } from 'solid-js';
import { cx } from '../cx';
import type { ClassMap } from './variant';

// A grid of cards. `gap="hairline"` is the canvas's "1px grid gaps instead of
// gutters": each cell is drawn with a 1px outline over a 1px gap, so the grid
// lines ARE the hairlines — an uneven last row leaves open space, never a
// rule-coloured block — and a `Panel` inside drops its own border. Columns
// collapse mobile-first (`_card-grid.scss`).
//
// `as="ul"` owns the list semantics: each child is wrapped in its own `<li>`, so
// a caller passes tiles, not list items.
export type CardGridColumns = 1 | 2 | 3 | 4;
export type CardGridGap = 'hairline' | 'sm' | 'md';

type CardGridBase = {
  readonly columns: CardGridColumns;
  readonly gap?: CardGridGap;
  readonly children: JSX.Element;
};

/** A list names itself (`aria-label`); a plain `div` is generic and cannot. */
export type CardGridProps =
  | (CardGridBase & { readonly as: 'ul'; readonly label?: string })
  | (CardGridBase & { readonly as?: 'div' });

export const CARD_GRID_COLUMNS_CLASS: ClassMap<`${CardGridColumns}`> = {
  '1': 'ui-grid--cols-1',
  '2': 'ui-grid--cols-2',
  '3': 'ui-grid--cols-3',
  '4': 'ui-grid--cols-4',
};

export const CARD_GRID_GAP_CLASS: ClassMap<CardGridGap> = {
  hairline: 'ui-grid--gap-hairline',
  sm: 'ui-grid--gap-sm',
  md: 'ui-grid--gap-md',
};

export function CardGrid(props: CardGridProps): JSX.Element {
  const cls = (): string =>
    cx(
      'ui-grid',
      CARD_GRID_COLUMNS_CLASS[`${props.columns}`],
      CARD_GRID_GAP_CLASS[props.gap ?? 'hairline'],
    );
  const items = children(() => props.children);
  return (
    <Switch>
      <Match when={props.as === 'ul'}>
        {/* `role="list"`: `.ui-grid` sets `list-style: none`, and Safari/VoiceOver
            drop a list's semantics when its marker is gone (see
            `styles/components/_card-grid.scss`). The role is redundant to biome,
            which has no CSS awareness to tell the two halves apart; the pairing
            is pinned by the guard in `src/styles.test.ts`. */}
        <ul class={cls()} role="list" aria-label={props.as === 'ul' ? props.label : undefined}>
          <For each={items.toArray()}>{(item) => <li class="ui-grid__item">{item}</li>}</For>
        </ul>
      </Match>
      <Match when={props.as !== 'ul'}>
        <div class={cls()}>{items()}</div>
      </Match>
    </Switch>
  );
}
